import React from 'react';
import { useAppContext } from '../App';

const CompareDrawer = () => {
  const {
    compareList,
    isCompareOpen,
    setIsCompareOpen,
    removeFromCompare,
    clearCompare,
    addToCart,
    showNotification
  } = useAppContext();

  if (!isCompareOpen || compareList.length === 0) return null;

  const handleAddToCart = (product) => {
    addToCart(product, 100);
  };

  const handleRequestQuote = () => {
    showNotification('Quote request sent for selected products!', 'success');
    clearCompare();
  };

  // Get all unique attributes across products
  const getAttributes = () => {
    return [
      { key: 'category', label: 'Category' },
      { key: 'purity', label: 'Purity' },
      { key: 'supplier', label: 'Supplier' },
      { key: 'casNumber', label: 'CAS Number' }
    ];
  };

  return (
    <>
      <div className="compare-overlay" onClick={() => setIsCompareOpen(false)} />
      <div className="compare-drawer">
        <div className="compare-header">
          <h3 className="compare-title">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="7" height="7" />
              <rect x="14" y="3" width="7" height="7" />
              <rect x="14" y="14" width="7" height="7" />
              <rect x="3" y="14" width="7" height="7" />
            </svg>
            Compare Products ({compareList.length}/4)
          </h3>
          <div className="compare-actions">
            <button className="btn btn-outline clear-all-btn" onClick={clearCompare}>
              Clear All
            </button>
            <button className="compare-close" onClick={() => setIsCompareOpen(false)}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>

        <div className="compare-content">
          <div className="compare-table">
            {/* Header row with product names */}
            <div className="compare-row header-row">
              <div className="compare-cell label-cell">Product</div>
              {compareList.map((product) => (
                <div key={product.id} className="compare-cell product-header">
                  <button
                    className="remove-product-btn"
                    onClick={() => removeFromCompare(product.id)}
                    aria-label="Remove from comparison"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                  <img src={product.image} alt={product.name} className="product-thumb" />
                  <span className="product-name">{product.name}</span>
                </div>
              ))}
              {/* Empty slots */}
              {[...Array(4 - compareList.length)].map((_, i) => (
                <div key={`empty-${i}`} className="compare-cell empty-slot">
                  <span>Add product to compare</span>
                </div>
              ))}
            </div>

            {/* Attribute rows */}
            {getAttributes().map((attr) => (
              <div key={attr.key} className="compare-row">
                <div className="compare-cell label-cell">{attr.label}</div>
                {compareList.map((product) => (
                  <div key={product.id} className="compare-cell">
                    {attr.key === 'category' ? (
                      <span className="category-badge">{product[attr.key]}</span>
                    ) : attr.key === 'casNumber' ? (
                      <span className="mono">{product[attr.key]}</span>
                    ) : (
                      product[attr.key]
                    )}
                  </div>
                ))}
                {[...Array(4 - compareList.length)].map((_, i) => (
                  <div key={`empty-${i}`} className="compare-cell empty-slot" />
                ))}
              </div>
            ))}

            {/* Actions row */}
            <div className="compare-row actions-row">
              <div className="compare-cell label-cell">Actions</div>
              {compareList.map((product) => (
                <div key={product.id} className="compare-cell">
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => handleAddToCart(product)}
                  >
                    Add to Cart
                  </button>
                </div>
              ))}
              {[...Array(4 - compareList.length)].map((_, i) => (
                <div key={`empty-${i}`} className="compare-cell empty-slot" />
              ))}
            </div>
          </div>
        </div>

        <div className="compare-footer">
          <button className="btn btn-accent request-quote-btn" onClick={handleRequestQuote}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
              <polyline points="10 9 9 9 8 9" />
            </svg>
            Request Quote for All
          </button>
        </div>

        <style jsx>{`
          .compare-overlay {
            position: fixed;
            inset: 0;
            background: rgba(0, 0, 0, 0.6);
            backdrop-filter: blur(4px);
            z-index: 200;
            animation: fadeIn 0.2s ease;
          }

          .compare-drawer {
            position: fixed;
            bottom: 0;
            left: 0;
            right: 0;
            max-height: 70vh;
            background: linear-gradient(180deg, rgba(30, 41, 59, 0.98), rgba(15, 23, 42, 0.98));
            border-top: 1px solid rgba(59, 130, 246, 0.2);
            border-radius: var(--radius-xl) var(--radius-xl) 0 0;
            z-index: 201;
            display: flex;
            flex-direction: column;
            animation: slideUp 0.3s ease;
          }

          .compare-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: var(--space-md) var(--space-xl);
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          }

          .compare-title {
            display: flex;
            align-items: center;
            gap: var(--space-sm);
            font-family: var(--font-display);
            font-size: var(--font-lg);
            font-weight: 600;
            color: var(--color-neutral-50);
            margin: 0;
          }

          .compare-actions {
            display: flex;
            align-items: center;
            gap: var(--space-md);
          }

          .clear-all-btn {
            padding: var(--space-xs) var(--space-md);
            font-size: var(--font-xs);
          }

          .compare-close {
            width: 36px;
            height: 36px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: rgba(255, 255, 255, 0.1);
            border: none;
            border-radius: var(--radius-md);
            color: var(--color-neutral-300);
            cursor: pointer;
            transition: all var(--transition-fast);
          }

          .compare-close:hover {
            background: rgba(239, 68, 68, 0.3);
            color: #ef4444;
          }

          .compare-content {
            flex: 1;
            overflow-x: auto;
            overflow-y: auto;
            padding: var(--space-lg);
          }

          .compare-table {
            display: flex;
            flex-direction: column;
            min-width: 800px;
          }

          .compare-row {
            display: flex;
            border-bottom: 1px solid rgba(255, 255, 255, 0.05);
          }

          .compare-row:last-child {
            border-bottom: none;
          }

          .compare-cell {
            flex: 1;
            min-width: 160px;
            padding: var(--space-md);
            display: flex;
            align-items: center;
            justify-content: center;
            text-align: center;
            font-size: var(--font-sm);
            color: var(--color-neutral-200);
          }

          .label-cell {
            flex: 0 0 120px;
            min-width: 120px;
            background: rgba(255, 255, 255, 0.03);
            font-weight: 500;
            color: var(--color-neutral-400);
            justify-content: flex-start;
            padding-left: var(--space-lg);
          }

          .product-header {
            flex-direction: column;
            gap: var(--space-sm);
            position: relative;
          }

          .remove-product-btn {
            position: absolute;
            top: 0;
            right: var(--space-sm);
            background: rgba(239, 68, 68, 0.2);
            border: none;
            border-radius: 50%;
            width: 24px;
            height: 24px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: var(--color-error);
            cursor: pointer;
            transition: all var(--transition-fast);
          }

          .remove-product-btn:hover {
            background: rgba(239, 68, 68, 0.4);
          }

          .product-thumb {
            width: 60px;
            height: 60px;
            border-radius: var(--radius-md);
            object-fit: cover;
          }

          .product-name {
            font-size: var(--font-xs);
            color: var(--color-neutral-100);
            text-align: center;
            line-height: 1.3;
          }

          .price-value {
            font-family: var(--font-display);
            font-weight: 600;
            color: var(--color-accent-300);
          }

          .category-badge {
            background: rgba(59, 130, 246, 0.2);
            color: var(--color-accent-400);
            padding: 2px var(--space-sm);
            border-radius: var(--radius-sm);
            font-size: var(--font-xs);
            text-transform: uppercase;
          }

          .mono {
            font-family: var(--font-mono);
            font-size: var(--font-xs);
          }

          .empty-slot {
            color: var(--color-neutral-600);
            font-style: italic;
            border: 2px dashed rgba(255, 255, 255, 0.1);
            border-radius: var(--radius-md);
            margin: var(--space-xs);
          }

          .actions-row .compare-cell {
            padding: var(--space-md);
          }

          .btn-sm {
            padding: var(--space-xs) var(--space-md);
            font-size: var(--font-xs);
          }

          .compare-footer {
            padding: var(--space-lg) var(--space-xl);
            border-top: 1px solid rgba(255, 255, 255, 0.1);
            display: flex;
            justify-content: center;
          }

          .request-quote-btn {
            display: flex;
            align-items: center;
            gap: var(--space-sm);
          }

          @keyframes slideUp {
            from {
              transform: translateY(100%);
            }
            to {
              transform: translateY(0);
            }
          }

          @media (max-width: 768px) {
            .compare-drawer {
              max-height: 80vh;
            }

            .compare-content {
              padding: var(--space-md);
            }
          }
        `}</style>
      </div>
    </>
  );
};

export default CompareDrawer;
